<template>
  <div>
    <DeleteModal
      modalId="modal-emergency-invoice-delete"
      @deleteToggle="deleteItem"
    />

    <b-modal
      :no-close-on-backdrop="true"
      id="modal-emergency-invoice-edit"
      ok-only
      centered
      size="lg"
      :title="
        createMode
          ? $t('emergency.Add Patient Emergency Invoice')
          : $t('emergency.Edit Patient Emergency Invoice')
      "
      @ok="submitItem()"
    >
      <b-card-text>
        <b-row>
          <div class="mt-2 mt-md-0 w-100 mx-1">
            <ServicePriceAutoComplete
              :label="$t('hospital.services')"
              :placeholder="$t('hospital.services')"
              :serializer="(val) => val"
              param-query="search"
              text-title="title"
              v-model="EditForm.services"
              :disableAmount="true"
              ref="serviceAutocomplete"
              type="emergency"
              :multiple="true"
            />
          </div>
        </b-row>
      </b-card-text>
    </b-modal>
    <b-card>
      <div>
        <!-- Data Table  -->
        <div class="d-flex justify-content-between mt-4 mb-2">
          <h2>{{ $t('emergency.Patient Emergency Invoice') }}</h2>
          <div class="d-flex align-items-center">
            <b-alert
              v-if="!isPatientCardActive && !isHistory"
              variant="warning"
              show
              class="mb-0 mr-2"
              style="font-size: 0.875rem;"
            >
              {{ $t('emergency.patient-card-inactive') }}
            </b-alert>
            <b-button
              variant="primary"
              @click="show(EditForm, 'create')"
              :disabled="!isPatientCardActive"
              v-if="
                $store.state.app.permissions.includes('store-service') &&
                !isHistory
              "
            >
              {{ $t('emergency.Add Patient Emergency Invoice') }}
            </b-button>
          </div>
        </div>

        <b-table
          :items="this.data"
          responsive
          :fields="tableColumns"
          primary-key="id"
          show-empty
          empty-text="No matching results found"
          class="position-relative"
          v-if="!loading"
        >
          <template #cell(invoice_no)="data">
            <span class="font-weight-bold text-primary">
              #{{ data.item.invoice_no }}
            </span>
          </template>

          <template #cell(service_items)="data">
            <div v-if="getNestedProperty(data.item, 'invoice_items.length', 0) > 0" class="">
              <!-- Show first item only -->
              <div 
                v-for="(item, index) in data.item.invoice_items.slice(0, 1)" 
                :key="index"
                class="test-item-row"
              >
                <div class="d-flex justify-content-between align-items-center py-1">
                  <div class="test-item-name">
                    <b-badge variant="primary" class="mr-1" style="font-size: 0.85rem;">
                      {{ index + 1 }}
                    </b-badge>
                    <span class="font-weight-bold text-dark">
                      {{ getNestedProperty(item, 'name', 'N/A') }}
                    </span>
                  </div>
                  <div class="test-item-amount">
                    <span class="text-success font-weight-bolder" style="font-size: 0.95rem;">
                      {{ formatPrice(getNestedProperty(item, 'net_amount', 0)) }}
                    </span>
                  </div>
                </div>
              </div>
              
              <!-- Show more button if more than 1 item -->
              <div v-if="data.item.invoice_items.length > 1" class="">
                <b-button
                  variant="outline-primary"
                  size="sm"
                  class="text-nowrap"
                  block
                  @click="showAllServiceItems(data.item)"
                >
                  <feather-icon icon="PlusIcon" size="14" class="mr-1" />
                  {{ $t('common.show') }} {{ data.item.invoice_items.length - 1 }} {{ $t('common.more-items') }}
                </b-button>
              </div>
            </div>
            <span v-else class="text-muted">{{ $t('common.no-items') }}</span>
          </template>

          <template #cell(discount)="data">
            <span class="text-nowrap font-weight-bold text-warning">
              {{ formatPrice(data.item.discount || 0) }}
            </span>
          </template>

          <template #cell(amount)="data">
            <span class="text-nowrap font-weight-bolder text-success">
              {{ formatPrice(data.item.amount) }}
            </span>
          </template>
          
          <!-- Status Column -->
          <template #cell(status)="data">
            <b-badge
              :variant="getStatusVariant(data.item.status)"
              class="text-capitalize"
              style="font-size: 1rem"
            >
              {{ getStatusText(data.item.status) }}
            </b-badge>
          </template>
          
          <!-- Column: Actions -->
          <template #cell(actions)="data">
            <div class="text-nowrap d-flex">
              <!-- <b-button
                v-if="data.item.status == 'approved' && data.item.status !== 'printed'"
                @click="showPrintInvoice(data.item)"
                variant="outline-primary"
                size="sm"
                class="mx-1"
              >
                {{ $t("reception.Print Invoice") }}
              </b-button> -->
              <!-- <b-button
                v-if="$store.state.app.permissions.includes('apply-invoice-discount') && data.item.status !== 'printed' && !isHistory"
                @click="showDiscountModal(data.item)"
                variant="outline-warning"
                size="sm"
                class="mx-1"
              >
                <feather-icon icon="PercentIcon" size="14" class="mr-1" />
                {{ $t("common.apply-discount") }}
              </b-button> -->
              <!-- Dropdown -->
              <ActionsButtons
                v-if="!isHistory"
                :noEdit="true"
                :noDelete="true"
                deletePerm="delete-invoice"
                editPerm="update-invoice"
                @editToggle="show(data.item)"
                @deleteToggle="deleteModel(data.item)"
              />
            </div>
          </template>
        </b-table>
        <template v-if="loading">
          <div>
            <div class="d-flex justify-content-center mb-1">
              <b-spinner label="Loading..." />
            </div>
          </div>
        </template>
        <div class="mx-2 mb-2">
          <b-row>
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted"
                >{{ $t('common.showing') }} 1 {{ $t('common.to') }} 10
              </span>
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
              @click="getPatientInvoiceEmergency()"
            >
              <b-pagination
                v-model="page"
                :total-rows="dataCount"
                :per-page="take"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="18" />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </div>
    </b-card>

    <!-- Modal to show all service items -->
    <b-modal
      id="modal-all-service-items"
      centered
      size="md"
      :title="$t('common.all-items') + ' - ' + $t('common.invoice-number') + ' #' + selectedInvoiceNo"
      hide-footer
    >
      <b-card-text>
        <div v-if="selectedInvoiceItems.length > 0">
          <div 
            v-for="(item, index) in selectedInvoiceItems" 
            :key="index"
            class="test-item-modal-row"
            :class="{ 'border-bottom': index < selectedInvoiceItems.length - 1 }"
          >
            <div class="d-flex justify-content-between align-items-center py-2">
              <div class="d-flex align-items-center flex-1">
                <b-badge variant="primary" class="mr-2" style="font-size: 0.9rem;">
                  {{ index + 1 }}
                </b-badge>
                <span class="font-weight-bold text-dark">
                  {{ getNestedProperty(item, 'name', 'N/A') }}
                </span>
              </div>
              <div class="ml-3">
                <span class="text-success font-weight-bolder" style="font-size: 1rem;">
                  {{ formatPrice(getNestedProperty(item, 'net_amount', 0)) }}
                </span>
              </div>
            </div>
          </div>
          
          <!-- Total -->
          <div class="mt-3 pt-3 border-top">
            <div class="d-flex justify-content-between align-items-center">
              <span class="font-weight-bold text-dark" style="font-size: 1.1rem;">
                {{ $t('common.total') }}:
              </span>
              <span class="text-success font-weight-bolder" style="font-size: 1.2rem;">
                {{ formatPrice(calculateModalTotal()) }}
              </span>
            </div>
          </div>
        </div>
      </b-card-text>
    </b-modal>

    <!-- Modal to apply discount -->
    <b-modal
      id="modal-apply-discount"
      centered
      size="md"
      :title="$t('common.apply-discount')"
      @ok="applyDiscount"
    >
      <b-card-text>
        <b-form-group 
          :label="$t('common.invoice-number')" 
          label-for="discount-invoice-no"
        >
          <b-form-input
            id="discount-invoice-no"
            :value="'#' + (discountForm.invoice_no || '')"
            disabled
          />
        </b-form-group>

        <b-form-group 
          :label="$t('common.current-amount')" 
          label-for="discount-current-amount"
        >
          <b-form-input
            id="discount-current-amount"
            :value="formatPrice(discountForm.amount)"
            disabled
          />
        </b-form-group>

        <b-form-group 
          :label="$t('common.discount-amount')" 
          label-for="discount-amount"
          :invalid-feedback="discountError"
          :state="discountError ? false : null"
        >
          <b-form-input
            id="discount-amount"
            v-model="discountForm.discount"
            type="number"
            min="0"
            :placeholder="$t('common.enter-discount-amount')"
            :state="discountError ? false : null"
          />
        </b-form-group>

        <b-alert v-if="discountForm.discount > 0" variant="info" show class="mt-2">
          <div class="d-flex justify-content-between">
            <span>{{ $t('common.amount-after-discount') }}:</span>
            <strong>{{ formatPrice(calculateAmountAfterDiscount()) }}</strong>
          </div>
        </b-alert>
      </b-card-text>
    </b-modal>

    <!-- Print Confirmation Modal -->
    <b-modal
      id="modal-confirm-print-emergency"
      :title="$t('surgery.print-invoice')"
      ok-only
      :ok-title="$t('common.print')"
      @ok="printInvoice"
    >
      <b-card-text>
        <h5>{{ $t("surgery.print-invoice") }}</h5>
        {{ $t("surgery.print-sure") }}
      </b-card-text>
    </b-modal>

    <!-- Hidden print button -->
    <b-button
      ref="EmergencyInvoicePrint"
      v-print="EmergencyInvoice"
      variant="outline-primary"
      size="sm"
      class="hidden"
    >
      {{ $t("reception.Print Invoice") }}
    </b-button>

    <EmergencyInvoice :data="invoice" />
  </div>
</template>

<script>
import {
  BCard,
  BFormGroup,
  BButton,
  BCol,
  BRow,
  BFormInput,
  BTable,
  BDropdown,
  BDropdownItem,
  BPagination,
  BSpinner,
  BBadge,
  BAlert,
  BCardText,
  BModal,
} from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import VuePhoneNumberInput from 'vue-phone-number-input';
import 'vue-phone-number-input/dist/vue-phone-number-input.css';
import { getNestedProperty } from '@/utils/nestedProperty.js';
const AutoComplete = () => import('@/components/common/AutoComplete.vue');
const EmergencyInvoice = () =>
  import('@/components/printables/EmergencyInvoice.vue');
const ServicePriceAutoComplete = () =>
  import('@/components/common/ServicePriceAutoComplete.vue');
  import ActionsButtons from '@/components/common/ActionsButtons.vue';
  import DeleteModal from '@/components/modals/DeleteModal.vue';
export default {
  components: {
    DeleteModal,
    ServicePriceAutoComplete,
    ActionsButtons,
    BCard,
    BFormGroup,
    BButton,
    BCol,
    BRow,
    BFormInput,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSpinner,
    VuePhoneNumberInput,
    AutoComplete,
    EmergencyInvoice,
    BBadge,
    BAlert,
    BCardText,
    BModal,
  },

  props: ['patientCardId', 'isHistory'],
  data() {
    return {
      take: 10,
      loading: true,
      isNotValid: false,
      dataCount: 0,
      data: [],
      EditForm: {
        patient_card_id: '',
        doctor_id: '',
        services: [],
        amount: 0,
        _method: 'PUT',
      },
      createMode: false,
      errorForm: {
        patient_card_id: this.$route.params.id,
        emergency_id: this.$route.params.emergency_id,
        services: [],
        amount: 0,
      },
      params: {
        form: {},
        path: '',
      },
      tableColumns: [
        {
          key: 'invoice_no',
          label: this.$t('common.invoice-number'),
          sortable: true,
        },
        {
          key: 'service_items',
          label: this.$t('reception.service'),
          sortable: false,
        },
        {
          key: 'discount',
          label: this.$t('common.discount'),
          sortable: true,
        },
        {
          key: 'amount',
          label: this.$t('employee.amount'),
          sortable: true,
        },
        {
          key: 'status',
          label: this.$t('common.status'),
          sortable: true,
        },
        {
          key: 'created_at',
          sortable: true,
          label: this.$t('common.created-at'),
        },
        { key: 'Actions', label: this.$t('common.actions') },
      ],
      currentPage: 1,
      takeOptions: [10, 25, 50, 100],
      searchQuery: '',
      sortBy: 'id',
      isSortDirDesc: true,
      page: 1,
      invoice: '',
      patientData: {
        name: this.$route.params.name,
      },
      patientCard: {
        is_active: true, // Default to true, will be updated from API
      },
      EmergencyInvoice: {
        id: 'EmergencyInvoice',
      },
      invoices: [],
      selectedInvoiceItems: [], // For modal display
      selectedInvoiceNo: '', // For modal title
      discountForm: {
        id: null,
        invoice_no: '',
        amount: 0,
        discount: 0
      },
      discountError: '',
    };
  },
  computed: {
    isPatientCardActive() {
      return this.patientCard.is_active;
    },
  },
  mounted() {
    this.getPatientInvoiceEmergency();
    this.getPatientCardInfo();
  },
  methods: {
    getNestedProperty,

    // Format price with currency
    formatPrice(price) {
      return price ? `${Number(price).toLocaleString()} IQD` : "0 IQD";
    },

    // Show all service items in modal
    showAllServiceItems(invoice) {
      this.selectedInvoiceItems = getNestedProperty(invoice, 'invoice_items', []);
      this.selectedInvoiceNo = invoice.invoice_no || '';
      this.$bvModal.show('modal-all-service-items');
    },
    
    // Calculate total for modal
    calculateModalTotal() {
      return this.selectedInvoiceItems.reduce((sum, item) => {
        const amount = getNestedProperty(item, 'net_amount', 0);
        return sum + (parseFloat(amount) || 0);
      }, 0);
    },

    // Show discount modal
    showDiscountModal(invoice) {
      this.discountForm = {
        id: invoice.id,
        invoice_no: invoice.invoice_no || '',
        amount: parseFloat(invoice.amount) || 0,
        discount: parseFloat(invoice.discount) || 0
      };
      this.discountError = '';
      this.$bvModal.show('modal-apply-discount');
    },

    // Calculate amount after discount
    calculateAmountAfterDiscount() {
      const amount = parseFloat(this.discountForm.amount) || 0;
      const discount = parseFloat(this.discountForm.discount) || 0;
      return Math.max(0, amount - discount);
    },

    // Apply discount to invoice
    async applyDiscount(bvModalEvt) {
      bvModalEvt.preventDefault();
      
      // Validate discount
      const discount = parseFloat(this.discountForm.discount);
      const amount = parseFloat(this.discountForm.amount);
      
      if (isNaN(discount) || discount < 0) {
        this.discountError = this.$t('common.invalid-discount');
        return;
      }
      
      if (discount > amount) {
        this.discountError = this.$t('common.discount-exceeds-amount');
        return;
      }

      try {
        this.loading = true;
        
        const payload = {
          discount: discount
        };

        await this.$http.put(`/invoices/${this.discountForm.id}`, payload);

        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.success"),
            variant: "success",
            text: this.$t("common.discount-applied-successfully"),
          },
        });

        this.$bvModal.hide('modal-apply-discount');
        
        // Refresh data
        this.getPatientInvoiceEmergency();
        
      } catch (error) {
        console.error("Error applying discount:", error);
        
        // Handle validation errors (400)
        if (error.response && error.response.status === 400) {
          const errors = error.response.data;
          
          // Check if discount field has errors
          if (errors.discount && Array.isArray(errors.discount)) {
            this.discountError = errors.discount[0];
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: this.$t("common.error"),
                variant: "danger",
                text: errors.discount[0],
              },
            });
          } else {
            // Generic validation error
            const firstError = Object.values(errors)[0];
            const errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
            this.discountError = errorMessage;
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: this.$t("common.error"),
                variant: "danger",
                text: errorMessage,
              },
            });
          }
        } else {
          // Generic error
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: this.$t("common.error"),
              variant: "danger",
              text: this.$t("common.discount-apply-error"),
            },
          });
        }
      } finally {
        this.loading = false;
      }
    },

    showPrintInvoice(item) {
      this.invoice = item;
      this.$bvModal.show('modal-confirm-print-emergency');
    },

    async printInvoice() {
      try {
        // Trigger print
        this.$refs.EmergencyInvoicePrint.click();
        
        // Update status to printed
        await this.$http.put(`/invoices/${this.invoice.id}`, {
          status: 'printed'
        });
        
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.success"),
            variant: "success",
            text: this.$t("common.invoice-printed-successfully"),
          },
        });
        
        // Refresh data
        this.getPatientInvoiceEmergency();
        
      } catch (error) {
        console.error("Error updating invoice status:", error);
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.error"),
            variant: "danger",
            text: this.$t("common.error-updating-status"),
          },
        });
      }
    },

    getStatusVariant(status) {
      switch(status) {
        case 'approved':
        case 'printed':
          return 'success';
        case 'pending':
          return 'warning';
        case 'rejected':
          return 'danger';
        default:
          return 'secondary';
      }
    },
    getStatusText(status) {
      switch(status) {
        case 'pending':
          return this.$t('common.pending');
        case 'approved':
          return this.$t('common.approved');
        case 'printed':
          return this.$t('common.printed');
        case 'rejected':
          return this.$t('common.rejected');
        default:
          return status || this.$t('common.unknown');
      }
    },
    show(item, mode) {
      if (mode === 'create') {
        this.createMode = true;
        this.EditForm = {
          patient_card_id: this.$route.params.id,
          services: [],
          amount: 0,
          _method: 'POST',
        };
      } else {
        this.createMode = false;

        this.EditForm.id = item.id;
        this.EditForm.patient_card_id = this.$route.params.id;
        this.EditForm.services = item.service ? [item.service] : [];
        this.EditForm.amount = item.amount;
        this.EditForm._method = 'PUT';
      }
      this.$bvModal.show('modal-emergency-invoice-edit');
      // Filter services to show only emergency type after modal is shown
      this.$nextTick(() => {
        this.filterEmergencyServices();
      });
    },
    serviceValue(service) {
      this.EditForm.service_id = service.id;
      this.EditForm.amount = service.price;
    },
    async filterEmergencyServices() {
      // Load emergency services directly
      if (this.$refs.serviceAutocomplete) {
        try {
          const response = await this.$http.get('/service?page=1&take=100&sort=-id');
          const allServices = response.data.data;
          const emergencyServices = allServices.filter(service => service.type === 'emergency');
          this.$refs.serviceAutocomplete.items = emergencyServices;
        } catch (error) {
          console.error('Error loading emergency services:', error);
        }
      }
    },
    async filterEmergencyServicesOnSearch(query) {
      // Filter emergency services based on search query
      if (this.$refs.serviceAutocomplete) {
        try {
          // Build search URL with query
          let url = `/service?page=1&take=100&sort=-id`;
          if (query && query.trim()) {
            url += `&filter[title]=${encodeURIComponent(query.trim())}`;
          }
          
          const response = await this.$http.get(url);
          const allServices = response.data.data;
          const emergencyServices = allServices.filter(service => service.type === 'emergency');
          this.$refs.serviceAutocomplete.items = emergencyServices;
        } catch (error) {
          console.error('Error searching emergency services:', error);
        }
      }
    },
    async getPatientCardInfo() {
      try {
        const response = await this.$http.get(`/patientCard/${this.$route.params.id}`);
        this.patientCard = response.data;
      } catch (error) {
        console.error('Error fetching patient card info:', error);
        // Keep default value if API fails
      }
    },
    async getPatientInvoiceEmergency() {
      this.loading = true;
      // check if there are any null value in the obj we sending

      let url = `/invoices?filter[type]=invoice_emergency&filter[patient_card_id]=${this.$route.params.id}&page=${this.page}&take=${this.take}&sort=-id&include=patientCard,service,invoiceItems`;

      await this.getList(url);
      this.dataCount = this.all_data.total;
      this.data = this.all_data.data;
      this.loading = false;
    },

    deleteModel(item) {
      this.deleteForm = item;
      this.$bvModal.show('modal-emergency-invoice-delete');
    },
    deleteItem() {
      this.$store
        .dispatch('delete', `/invoices/${this.deleteForm.id}`)
        .then((response) => {
          this.getPatientInvoiceEmergency();

          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Delete Success',
              icon: 'success',
              variant: 'info',
              text: 'deleted ' + this.deleteForm.service.title + ' successfully',
            },
          });
        })
        .catch((error) => {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Error',
              variant: 'danger',
              text: 'Error deleting ' + this.deleteForm.service.title,
            },
          });
        });
    },
    submitItem() {
      for (let item in this.errorForm) {
        this.errorForm[item] = '';
        if (this.EditForm[item] == '' || this.EditForm[item] == null)
          delete this.EditForm[item];
      }

      if (this.createMode) {
        // Validate that services are selected
        if (!this.EditForm.services || this.EditForm.services.length === 0) {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Validation Error',
              variant: 'danger',
              text: this.$t('emergency.please-select-services'),
            },
          });
          return;
        }

        // Build items array from selected services
        const items = this.EditForm.services.map(service => ({
          service_id: service.id
        }));

        // Create invoice payload according to API requirements
        const invoicePayload = {
          patient_card_id: this.$route.params.id,
          type: 'invoice_emergency',
          emergency_id: this.$route.params.emergency_id,
          items: items
        };

        this.params.form = { invoices: [invoicePayload] };
        this.params.path = `/invoices`;
        
        this.$store
          .dispatch('create', this.params)
          .then((response) => {
            this.getPatientInvoiceEmergency();
            this.$bvModal.hide('modal-emergency-invoice-edit');
            this.$toast({
              component: ToastificationContent,
              position: 'top-right',
              props: {
                title: 'Success',
                variant: 'success',
                text: this.$t('common.saved-successfully'),
              },
            });
          })
          .catch((error) => {
            if (error.response && error.response.status === 400) {
              for (let item in error.response.data) {
                this.$toast({
                  component: ToastificationContent,
                  position: 'top-right',
                  props: {
                    title: 'validation error',
                    variant: 'danger',
                    text: error.response.data[item][0],
                  },
                });
              }
            }
          });
      } else {
        (this.params.form = this.EditForm),
          (this.params.path = `/invoices/${this.EditForm.id}`),
          this.$store
            .dispatch('update', this.params)
            .then((response) => {
              this.getPatientInvoiceEmergency();
              this.$bvModal.hide('modal-emergency-invoice-edit');
            })
            .catch((error) => {
              if (error.response && error.response.status === 400) {
                for (let item in error.response.data) {
                  this.$toast({
                    component: ToastificationContent,
                    position: 'top-right',
                    props: {
                      title: 'validation error',
                      variant: 'danger',
                      text: error.response.data[item][0],
                    },
                  });
                }
              }
            });
      }
    },
  },
};
</script>
<style scoped>
/* CSS */
.button-preview {
  align-items: center;
  appearance: none;
  background-color: #fff;
  border: 1px solid #dbdbdb;
  border-radius: 0.375em;
  box-shadow: none;
  box-sizing: border-box;
  color: #363636;
  cursor: pointer;
  display: inline-flex;
  font-family: BlinkMacSystemFont, -apple-system, 'Segoe UI', Roboto, Oxygen,
    Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', Helvetica,
    Arial, sans-serif;
  font-size: 1rem;
  height: 2.5em;
  justify-content: center;
  line-height: 1.5;
  padding: calc(0.5em - 1px) 1em;
  position: relative;
  text-align: center;
  user-select: none;
  -webkit-user-select: none;
  touch-action: manipulation;
  vertical-align: top;
  white-space: nowrap;
  margin-bottom: 3px;
  margin-right: 10px;
}

.button-preview:active {
  border-color: #4a4a4a;
  outline: 0;
}

.button-preview:focus {
  border-color: #485fc7;
  outline: 0;
}

.button-preview:hover {
  border-color: #b5b5b5;
  background: rgb(245, 241, 241);
}

.button-preview:focus:not(:active) {
  box-shadow: rgba(72, 95, 199, 0.25) 0 0 0 0.125em;
}

.modify {
  height: 6rem;
  font-size: 2rem;
  align-items: center;
}

.modify:hover {
  box-shadow: rgba(0, 0, 0, 0.35) 0px 5px 15px;
}

.hidden {
  display: none !important;
}

/* Service Items Container Styling */
.test-items-container {
  min-width: 250px;
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 8px;
  border: 1px solid #e9ecef;
}

.test-item-row {
  padding: 6px 0;
}

.test-item-row.border-bottom {
  border-bottom: 1px solid #dee2e6 !important;
}

.test-item-name {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.test-item-amount {
  margin-left: 12px;
  white-space: nowrap;
}

/* Modal test items styling */
.test-item-modal-row {
  padding: 8px 0;
}

.test-item-modal-row.border-bottom {
  border-bottom: 1px solid #e9ecef !important;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
